import { useCallback, useEffect, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';

import { ITourStep, TTourId, TOUR_CONFIGS } from './tourConfig';

const TOUR_INTERACTED_KEY = 'bds-tour-interacted';
const TOUR_SEEN_PREFIX = 'bds-tour-seen-';

/** Check if user has ANY tour-related key in localStorage */
const hasAnyTourKey = (userId: string): boolean => {
  // Check global interacted key
  if (localStorage.getItem(`${userId}-${TOUR_INTERACTED_KEY}`)) return true;
  // Check per-tour seen keys
  const tourIds: TTourId[] = ['project', 'challenge', 'practice', 'codeframe'];
  return tourIds.some((id) => localStorage.getItem(`${userId}-${TOUR_SEEN_PREFIX}${id}`));
};

export interface IUseTourReturn {
  steps: ITourStep[];
  currentStep: number;
  isActive: boolean;
  isProminent: boolean;
  isAutoStarted: boolean;
  start: () => void;
  next: () => void;
  prev: () => void;
  stop: () => void;
  skip: () => void;
  totalSteps: number;
}

const useTour = (tourId: TTourId | null): IUseTourReturn => {
  const { id: userId } = useCurrentUser();
  const steps = tourId ? TOUR_CONFIGS[tourId] : [];

  const [currentStep, setCurrentStep] = useState(-1);
  const [isActive, setIsActive] = useState(false);
  const [isAutoStarted, setIsAutoStarted] = useState(false);
  const [hasInteractedAny, setHasInteractedAny] = useState(true);
  const autoStartedRef = useRef(false);

  useEffect(() => {
    if (!userId || !tourId) return;
    setHasInteractedAny(hasAnyTourKey(userId));
  }, [userId, tourId]);

  const markInteracted = useCallback(() => {
    if (!userId || !tourId) return;
    localStorage.setItem(`${userId}-${TOUR_INTERACTED_KEY}`, 'true');
    localStorage.setItem(`${userId}-${TOUR_SEEN_PREFIX}${tourId}`, 'true');
    setHasInteractedAny(true);
  }, [userId, tourId]);

  const start = useCallback(() => {
    if (!steps.length) return;
    setIsAutoStarted(false);
    setCurrentStep(0);
    setIsActive(true);
    markInteracted();
  }, [steps.length, markInteracted]);

  const next = useCallback(() => {
    setCurrentStep((p) => {
      if (p >= steps.length - 1) {
        setIsActive(false);
        return -1;
      }
      return p + 1;
    });
  }, [steps.length]);

  const prev = useCallback(() => {
    setCurrentStep((p) => Math.max(0, p - 1));
  }, []);

  const stop = useCallback(() => {
    setIsActive(false);
    setCurrentStep(-1);
    markInteracted();
  }, [markInteracted]);

  const skip = useCallback(() => {
    setIsActive(false);
    setCurrentStep(-1);
    markInteracted();
  }, [markInteracted]);

  // Auto-start the tour on first ever visit (delayed so DOM is settled)
  useEffect(() => {
    if (!userId || !tourId || !steps.length || autoStartedRef.current) return;
    if (hasInteractedAny) return;

    autoStartedRef.current = true;
    const timeout = setTimeout(() => {
      setIsAutoStarted(true);
      setCurrentStep(0);
      setIsActive(true);
      // Mark as interacted immediately so it never auto-starts again
      markInteracted();
    }, 800);

    return () => clearTimeout(timeout);
  }, [userId, tourId, steps.length, hasInteractedAny, markInteracted]);

  return {
    steps,
    currentStep,
    isActive,
    isProminent: !hasInteractedAny && !!tourId,
    isAutoStarted,
    start,
    next,
    prev,
    stop,
    skip,
    totalSteps: steps.length,
  };
};

export default useTour;
